import {fetchAuthSession} from 'aws-amplify/auth';
import type {ZodType, z} from 'zod';
import type {ErrorParams, FieldError} from '@afroep/core/errors/error-code';
import {buildPath, type Route, type RouteParams} from '@afroep/core/http/routes';
import {reportSessionExpired} from './session-expiry';

/**
 * A refusal from our own API. It carries a code and the values a sentence needs, never a
 * sentence — the words are chosen here, in the reader's language, by `useApiError`.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly params?: ErrorParams,
    readonly fields?: FieldError[],
  ) {
    // For a stack trace and for Sentry. Never shown to anyone.
    super(code);
    this.name = 'ApiError';
  }
}

const baseUrl = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

/**
 * API Gateway's JWT authorizer checks the `aud` claim, which Cognito only puts on the
 * ID token — sending the access token here would fail with a bare 401.
 */
const authorization = async (): Promise<string> => {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw unauthenticated();
  return `Bearer ${token}`;
};

/**
 * Both ways a session ends up gone — no token to send, or a token the authorizer
 * rejected — report it, so the reader is taken to sign in rather than left clicking.
 */
const unauthenticated = (): ApiError => {
  reportSessionExpired();
  return new ApiError(401, 'UNAUTHENTICATED');
};

export type QueryParams = Record<string, string | number | boolean | undefined>;

const toSearch = (query: QueryParams | undefined): string => {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `?${search}` : '';
};

const parseError = async (response: Response): Promise<ApiError> => {
  const body = (await response.json().catch(() => null)) as {
    code?: string;
    params?: ErrorParams;
    fields?: FieldError[];
  } | null;
  /**
   * A body we cannot read means something in front of the API answered — a gateway, a
   * proxy. There is no code to translate, so it is treated as any other unknown failure.
   *
   * A 401 is the exception, because API Gateway's own JWT authorizer answers
   * `{"message":"Unauthorized"}` and never a code of ours. Read as unknown it becomes
   * "something went wrong on our side", which blames us for a lapsed session the reader
   * can fix in five seconds.
   */
  const fallback = response.status === 401 ? 'UNAUTHENTICATED' : 'INTERNAL_ERROR';
  return new ApiError(response.status, body?.code ?? fallback, body?.params, body?.fields);
};

export interface RequestOptions<T> {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  schema?: ZodType<T>;
  body?: unknown;
  query?: QueryParams;
  /**
   * Send no token. Only for the endpoints whose whole point is that the caller cannot
   * sign in — the password reset. Everything else must stay authenticated, so this is
   * opt-in per call rather than a fallback when a session happens to be missing.
   */
  anonymous?: boolean;
}

export const apiRequest = async <T>(options: RequestOptions<T>): Promise<T> => {
  const response = await fetch(`${baseUrl}${options.path}${toSearch(options.query)}`, {
    method: options.method,
    headers: {
      ...(options.anonymous ? {} : {Authorization: await authorization()}),
      ...(options.body === undefined ? {} : {'Content-Type': 'application/json'}),
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (!response.ok) {
    const error = await parseError(response);
    if (error.code === 'UNAUTHENTICATED') reportSessionExpired();
    throw error;
  }
  if (response.status === 204 || !options.schema) return undefined as T;

  return options.schema.parse(await response.json());
};

/** A route's response type: `void` for the endpoints that answer 204. */
type ResponseOf<R extends Route> = R['response'] extends ZodType ? z.output<R['response']> : void;

interface Payload {
  body?: unknown;
  query?: QueryParams;
}

/**
 * What a given route needs. `params` is derived from the route's own path, so it is
 * required — with the right keys — for `/api/users/{id}` and rejected outright for
 * `/api/users`.
 */
export type CallOptions<R extends Route> = keyof RouteParams<R> extends never
  ? Payload & {params?: never}
  : Payload & {params: RouteParams<R>};

/**
 * The options argument disappears for a route that needs nothing, and is mandatory for a
 * route with a path parameter. That is what makes `call(ROUTES.getUser)` a compile error
 * instead of a runtime throw — the same guarantee `pathTo` gives its own callers.
 */
type CallArgs<R extends Route> = keyof RouteParams<R> extends never
  ? [options?: CallOptions<R>]
  : [options: CallOptions<R>];

/**
 * The only way this application talks to its own API. The method, the path and the schema
 * the response is validated against all come from the route manifest in `core`, so a
 * renamed endpoint is a compile error here rather than a 404 in front of a user.
 */
export const call = <R extends Route>(route: R, ...args: CallArgs<R>): Promise<ResponseOf<R>> => {
  const options = args[0] ?? ({} as CallOptions<R>);
  return apiRequest({
    method: route.method,
    // `buildPath` rather than `pathTo`: this function has just checked its own caller's
    // parameters, and `pathTo`'s tuple cannot be resolved for an unresolved `R`.
    path: buildPath(route, options.params),
    schema: route.response as ZodType<ResponseOf<R>> | undefined,
    body: options.body,
    query: options.query,
    ...(route.public ? {anonymous: true} : {}),
  });
};
